import React, { useState, useRef, useEffect } from 'react';
import { toPng } from 'html-to-image';
import { Download, Calendar, RefreshCw, Plus, X, Upload, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api, BACKEND_URL } from '@/api';

const ASPECT_RATIOS = {
  instagram: { width: 1080, height: 1080, label: 'Instagram (1:1)' },
  instagramTall: { width: 1080, height: 1350, label: 'Instagram (4:5)' },
  story: { width: 1080, height: 1920, label: 'Story (9:16)' },
};

export const WeeklyScheduleGenerator = () => {
  const [allGames, setAllGames] = useState([]);
  const [games, setGames] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [aspectRatio, setAspectRatio] = useState('instagramTall');
  const [generating, setGenerating] = useState(false);
  const [title, setTitle] = useState('WEEKLY SCHEDULE');
  const [sponsors, setSponsors] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [backgroundOpacity, setBackgroundOpacity] = useState(30);
  
  // Filter states
  const [sports, setSports] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedSport, setSelectedSport] = useState('all');
  const [selectedLeague, setSelectedLeague] = useState('all');
  const [selectedTeam, setSelectedTeam] = useState('all');
  
  const graphicRef = useRef(null);
  const fileInputRef = useRef(null);
  const bgFileInputRef = useRef(null);

  // Load filter options on mount
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const [sportsRes, leaguesRes, teamsRes] = await Promise.all([
          api.getSports(),
          api.getLeagues(),
          api.getTeams()
        ]);
        setSports(sportsRes.data || []);
        setLeagues(leaguesRes.data || []);
        setTeams(teamsRes.data || []);
      } catch (error) {
        console.error('Error loading filter options:', error);
      }
    };
    loadFilterOptions();
  }, []);

  // Set default dates (this week)
  useEffect(() => {
    const today = new Date();
    const nextWeek = new Date(today);
    nextWeek.setDate(today.getDate() + 7);
    
    setStartDate(today.toISOString().split('T')[0]);
    setEndDate(nextWeek.toISOString().split('T')[0]);
  }, []);

  // Load games when dates change
  useEffect(() => {
    if (startDate && endDate) {
      loadGames();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  // Apply filters when filter selections or allGames change
  useEffect(() => {
    applyFilters();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allGames, selectedSport, selectedLeague, selectedTeam]);

  const loadGames = async () => {
    try {
      const res = await api.getGames();
      // Filter games within date range
      const filtered = res.data.filter(game => {
        if (!game.scheduled_time) return false;
        const gameDate = new Date(game.scheduled_time).toISOString().split('T')[0];
        return gameDate >= startDate && gameDate <= endDate;
      });
      
      // Sort by date and time
      filtered.sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time));
      setAllGames(filtered);
    } catch (error) {
      console.error('Error loading games:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...allGames];
    
    // Filter by sport
    if (selectedSport !== 'all') {
      const sportLeagues = leagues.filter(l => l.sport_id === selectedSport).map(l => l.id);
      filtered = filtered.filter(game => sportLeagues.includes(game.league_id));
    }
    
    // Filter by league
    if (selectedLeague !== 'all') {
      filtered = filtered.filter(game => game.league_id === selectedLeague);
    }
    
    // Filter by team
    if (selectedTeam !== 'all') {
      filtered = filtered.filter(game => 
        game.home_team_id === selectedTeam || game.away_team_id === selectedTeam
      );
    }
    
    setGames(filtered);
  };

  // Get filtered leagues based on selected sport
  const getFilteredLeagues = () => {
    if (selectedSport === 'all') return leagues;
    return leagues.filter(l => l.sport_id === selectedSport);
  };

  // Get filtered teams based on selected league
  const getFilteredTeams = () => {
    if (selectedLeague === 'all' && selectedSport === 'all') return teams;
    if (selectedLeague !== 'all') {
      return teams.filter(t => t.league_ids?.includes(selectedLeague) || t.league_id === selectedLeague);
    }
    if (selectedSport !== 'all') {
      const sportLeagueIds = leagues.filter(l => l.sport_id === selectedSport).map(l => l.id);
      return teams.filter(t => 
        t.league_ids?.some(lid => sportLeagueIds.includes(lid)) || 
        sportLeagueIds.includes(t.league_id)
      );
    }
    return teams;
  };

  // Group games by date, then by league
  const groupGamesByDateAndLeague = () => {
    const grouped = {};
    
    games.forEach(game => {
      const date = new Date(game.scheduled_time);
      const dateKey = date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        month: 'short', 
        day: 'numeric' 
      }).toUpperCase();
      
      if (!grouped[dateKey]) {
        grouped[dateKey] = {};
      }
      
      const leagueName = game.league_name || 'Other';
      if (!grouped[dateKey][leagueName]) {
        grouped[dateKey][leagueName] = [];
      }
      
      grouped[dateKey][leagueName].push(game);
    });
    
    return grouped;
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    }).replace(' ', '').toLowerCase();
  };

  // Calculate dynamic font size based on text length to fit within available width
  const getTeamFontSize = (homeTeam, awayTeam, maxWidth) => {
    const text = `${homeTeam} v. ${awayTeam}`;
    const baseSize = 30; // Base font size in pixels
    const minSize = 16; // Minimum font size
    const charWidthRatio = 0.55; // Approximate character width ratio for Montserrat semibold
    
    const estimatedWidth = text.length * baseSize * charWidthRatio;
    
    if (estimatedWidth <= maxWidth) {
      return baseSize;
    }
    
    // Scale down proportionally
    const scaleFactor = maxWidth / estimatedWidth;
    return Math.max(Math.floor(baseSize * scaleFactor), minSize);
  };

  const isPlusGame = (game) => {
    if (!game.video_sources || game.video_sources.length === 0) return false;
    return game.video_sources.some(source => source.is_free === false);
  };

  const hasVideo = (game) => {
    return game.video_sources && game.video_sources.length > 0;
  };

  // Sponsor logo upload handler
  const handleSponsorUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        const res = await api.uploadMedia(formData);
        if (res.data && res.data.url) {
          setSponsors(prev => [...prev, {
            id: Date.now() + Math.random(),
            url: res.data.url,
            name: file.name.replace(/\.[^/.]+$/, '')
          }]);
        }
      }
    } catch (error) {
      console.error('Error uploading sponsor logo:', error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeSponsor = (id) => {
    setSponsors(prev => prev.filter(s => s.id !== id));
  };

  // Handle background image upload
  const handleBackgroundUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setBackgroundImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeBackground = () => {
    setBackgroundImage(null);
    if (bgFileInputRef.current) {
      bgFileInputRef.current.value = '';
    }
  };

  const handleDownload = async () => {
    if (!graphicRef.current) return;
    
    setGenerating(true);
    try {
      const { width, height } = ASPECT_RATIOS[aspectRatio];
      
      // Temporarily remove the scale transform for accurate export
      const originalTransform = graphicRef.current.style.transform;
      graphicRef.current.style.transform = 'none';
      
      const dataUrl = await toPng(graphicRef.current, {
        width,
        height,
        pixelRatio: 2,
        backgroundColor: '#0f172a',
        style: {
          transform: 'none',
        },
      });
      
      // Restore the transform
      graphicRef.current.style.transform = originalTransform;
      
      const link = document.createElement('a');
      link.download = `schedule-${startDate}-to-${endDate}.png`;
      link.href = dataUrl;
      link.click();
    } catch (error) {
      console.error('Error generating graphic:', error);
    } finally {
      setGenerating(false);
    }
  };

  // Helper to resolve media URL
  const resolveUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  const { width, height } = ASPECT_RATIOS[aspectRatio];
  const scale = Math.min(500 / width, 700 / height);
  const groupedGames = groupGamesByDateAndLeague();

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <Label className="text-gray-300 mb-2 block">Start Date</Label>
          <Input 
            type="date" 
            value={startDate} 
            onChange={(e) => setStartDate(e.target.value)}
            className="bg-gray-700 border-gray-600 text-white"
          />
        </div>
        <div>
          <Label className="text-gray-300 mb-2 block">End Date</Label>
          <Input 
            type="date" 
            value={endDate} 
            onChange={(e) => setEndDate(e.target.value)}
            className="bg-gray-700 border-gray-600 text-white"
          />
        </div>
        <div>
          <Label className="text-gray-300 mb-2 block">Title</Label>
          <Input 
            value={title} 
            onChange={(e) => setTitle(e.target.value)}
            placeholder="WEEKLY SCHEDULE"
            className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
        <div className="flex items-center gap-2 col-span-full mb-2">
          <Filter className="w-4 h-4 text-sky-400" />
          <span className="text-gray-300 font-medium">Filter Games</span>
          <span className="text-gray-500 text-sm">
            ({games.length} game{games.length !== 1 ? 's' : ''} found)
          </span>
          {(selectedSport !== 'all' || selectedLeague !== 'all' || selectedTeam !== 'all') && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelectedSport('all');
                setSelectedLeague('all');
                setSelectedTeam('all');
              }}
              className="text-gray-400 hover:text-white ml-auto"
            >
              Clear Filters
            </Button>
          )}
        </div>
        <div>
          <Label className="text-gray-400 mb-1 block text-sm">Sport</Label>
          <Select value={selectedSport} onValueChange={(val) => {
            setSelectedSport(val);
            setSelectedLeague('all');
            setSelectedTeam('all');
          }}>
            <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
              <SelectValue placeholder="All Sports" />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700">
              <SelectItem value="all" className="text-white">All Sports</SelectItem>
              {sports.map(sport => (
                <SelectItem key={sport.id} value={sport.id} className="text-white">
                  {sport.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-gray-400 mb-1 block text-sm">Competition</Label>
          <Select value={selectedLeague} onValueChange={(val) => {
            setSelectedLeague(val);
            setSelectedTeam('all');
          }}>
            <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
              <SelectValue placeholder="All Competitions" />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700">
              <SelectItem value="all" className="text-white">All Competitions</SelectItem>
              {getFilteredLeagues().map(league => (
                <SelectItem key={league.id} value={league.id} className="text-white">
                  {league.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-gray-400 mb-1 block text-sm">Team</Label>
          <Select value={selectedTeam} onValueChange={setSelectedTeam}>
            <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
              <SelectValue placeholder="All Teams" />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700 max-h-60">
              <SelectItem value="all" className="text-white">All Teams</SelectItem>
              {getFilteredTeams().map(team => (
                <SelectItem key={team.id} value={team.id} className="text-white">
                  {team.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Sponsor Logos */}
      <div>
        <Label className="text-gray-300 mb-2 block">Sponsor Logos</Label>
        <div className="flex flex-wrap items-center gap-3 p-3 bg-gray-800 rounded-lg min-h-[60px]">
          {sponsors.map(sponsor => (
            <div 
              key={sponsor.id} 
              className="relative group bg-gray-700 p-2 rounded flex items-center"
            >
              <img 
                src={resolveUrl(sponsor.url)} 
                alt={sponsor.name} 
                className="h-8 w-auto object-contain"
              />
              <button
                onClick={() => removeSponsor(sponsor.id)}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleSponsorUpload}
            className="hidden"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="border-dashed border-gray-500 text-gray-400 hover:text-white hover:border-gray-400"
          >
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? 'Uploading...' : 'Add Sponsor'}
          </Button>
        </div>
        <p className="text-gray-500 text-xs mt-1">Upload sponsor logos to display at the bottom of the graphic</p>
      </div>

      {/* Background Image */}
      <div>
        <Label className="text-gray-300 mb-2 block">Background Image (Optional)</Label>
        <div className="flex items-center gap-4">
          <input
            ref={bgFileInputRef}
            type="file"
            accept="image/*"
            onChange={handleBackgroundUpload}
            className="hidden"
          />
          {backgroundImage ? (
            <div className="flex items-center gap-3">
              <img 
                src={backgroundImage} 
                alt="Background" 
                className="h-16 w-24 object-cover rounded border border-gray-600"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={removeBackground}
                className="text-red-400 border-red-400 hover:bg-red-400/20"
              >
                <X className="w-4 h-4 mr-1" /> Remove
              </Button>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => bgFileInputRef.current?.click()}
              className="border-dashed border-gray-500 text-gray-400 hover:text-white hover:border-gray-400"
            >
              <Upload className="w-4 h-4 mr-2" />
              Add Background
            </Button>
          )}
          
          {backgroundImage && (
            <div className="flex items-center gap-3">
              <Label className="text-gray-400 text-sm">Opacity:</Label>
              <input
                type="range"
                min="10"
                max="80"
                value={backgroundOpacity}
                onChange={(e) => setBackgroundOpacity(Number(e.target.value))}
                className="w-32 accent-sky-500"
              />
              <span className="text-gray-400 text-sm w-10">{backgroundOpacity}%</span>
            </div>
          )}
        </div>
        <p className="text-gray-500 text-xs mt-1">Add a background image with adjustable transparency for better text legibility</p>
      </div>

      <div className="flex gap-4 items-end">
        <div>
          <Label className="text-gray-300 mb-2 block">Aspect Ratio</Label>
          <Select value={aspectRatio} onValueChange={setAspectRatio}>
            <SelectTrigger className="w-48 bg-gray-700 border-gray-600 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-gray-800 border-gray-700">
              {Object.entries(ASPECT_RATIOS).map(([key, { label }]) => (
                <SelectItem key={key} value={key} className="text-white">{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <Button onClick={loadGames} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh Games
        </Button>
        
        <Button 
          onClick={handleDownload} 
          disabled={generating || games.length === 0}
          className="bg-sky-600 hover:bg-sky-700"
        >
          <Download className="w-4 h-4 mr-2" />
          {generating ? 'Generating...' : 'Download Schedule'}
        </Button>
      </div>

      {/* Preview */}
      <div className="flex justify-center">
        <div 
          className="border border-gray-700 rounded-lg overflow-hidden"
          style={{ 
            width: width * scale, 
            height: height * scale,
          }}
        >
          <div
            ref={graphicRef}
            style={{
              width: `${width}px`,
              height: `${height}px`,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
              fontFamily: "'Montserrat', sans-serif",
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Background Image */}
            {backgroundImage && (
              <div 
                style={{
                  position: 'absolute',
                  inset: 0,
                  backgroundImage: `url(${backgroundImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  opacity: backgroundOpacity / 100,
                }}
              />
            )}
            
            {/* Dark overlay for text legibility */}
            <div 
              style={{
                position: 'absolute',
                inset: 0,
                background: backgroundImage 
                  ? `linear-gradient(180deg, rgba(15, 23, 42, ${1 - backgroundOpacity/100 + 0.3}) 0%, rgba(30, 41, 59, ${1 - backgroundOpacity/100 + 0.3}) 100%)`
                  : 'transparent',
              }}
            />

            {/* Content (positioned above background) */}
            <div style={{ position: 'relative', zIndex: 1, height: '100%' }}>
              {/* Header */}
              <div className="text-center pt-12 pb-8">
                <img 
                  src="/nls-logo.svg" 
                  alt="NLS" 
                  className="mx-auto mb-6"
                  style={{ height: '120px' }}
                />
              <h1 
                className="text-6xl font-black text-amber-400 tracking-wide"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                {title}
              </h1>
              <p className="text-gray-400 text-2xl mt-3">
                {new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </p>
            </div>

            {/* Schedule Content */}
            <div 
              className="px-6 overflow-hidden"
              style={{ maxHeight: height - 350 }}
            >
              {Object.keys(groupedGames).length === 0 ? (
                <div className="text-center text-gray-500 py-12">
                  <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No games scheduled for this period</p>
                </div>
              ) : (
                Object.entries(groupedGames).map(([dateKey, leagues]) => (
                  <div key={dateKey} className="mb-8">
                    {/* Date Header */}
                    <div className="border-b-2 border-sky-500 pb-3 mb-4">
                      <h2 className="text-3xl font-bold text-white">{dateKey}</h2>
                    </div>
                    
                    {/* Leagues for this date */}
                    {Object.entries(leagues).map(([leagueName, leagueGames]) => (
                      <div key={leagueName} className="mb-6">
                        {/* League Header */}
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-sky-400 text-4xl font-bold uppercase tracking-wide">
                            {leagueName}
                          </span>
                        </div>
                        
                        {/* Games */}
                        {leagueGames.map((game, idx) => {
                          // Calculate available width for team names (95% of graphic width minus time and logo)
                          const availableWidth = width * 0.95 - 180; // 180px for time + logo area
                          const fontSize = getTeamFontSize(game.home_team_name, game.away_team_name, availableWidth);
                          
                          return (
                            <div 
                              key={game.id || idx}
                              className="flex items-center justify-between py-3 border-b border-gray-700/50"
                              style={{ width: '95%', margin: '0 auto' }}
                            >
                              <div className="flex items-center flex-1 min-w-0">
                                <span 
                                  className="text-gray-400 font-medium flex-shrink-0" 
                                  style={{ fontSize: `${Math.max(fontSize, 20)}px`, minWidth: '100px', marginRight: '20px' }}
                                >
                                  {formatTime(game.scheduled_time)}
                                </span>
                                <span 
                                  className="text-white font-semibold whitespace-nowrap"
                                  style={{ fontSize: `${fontSize}px` }}
                                >
                                  {game.home_team_name || 'TBD'} <span className="text-gray-500">v.</span> {game.away_team_name || 'TBD'}
                                </span>
                              </div>
                              
                              {/* Platform Logo */}
                              <div className="flex-shrink-0 ml-4">
                              {hasVideo(game) ? (
                                isPlusGame(game) ? (
                                  <div className="bg-amber-500 text-black text-xl font-bold px-4 py-2 rounded">
                                    NLS+
                                  </div>
                                ) : (
                                  <svg viewBox="0 0 24 24" className="w-12 h-12 text-red-500 fill-current">
                                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                                  </svg>
                                )
                              ) : (
                                <span className="text-gray-600 text-xl">TBD</span>
                              )}
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>

            {/* Footer - Sponsor Logos */}
            <div 
              className="absolute bottom-0 left-0 right-0 py-6 px-8"
              style={{ background: 'linear-gradient(0deg, #0f172a 0%, transparent 100%)' }}
            >
              {sponsors.length > 0 ? (
                <div className="flex items-center justify-center" style={{ gap: '10px' }}>
                  {sponsors.map(sponsor => (
                    <img 
                      key={sponsor.id}
                      src={resolveUrl(sponsor.url)} 
                      alt={sponsor.name}
                      style={{ height: '30px', width: 'auto' }}
                      className="object-contain"
                    />
                  ))}
                </div>
              ) : (
                <p className="text-gray-600 text-sm text-center">Add sponsor logos above</p>
              )}
            </div>
            </div> {/* End content wrapper */}
          </div>
        </div>
      </div>

      {/* Games Count */}
      <p className="text-center text-gray-500 text-sm">
        {games.length} game{games.length !== 1 ? 's' : ''} scheduled • Output: {width}×{height}px
      </p>
    </div>
  );
};

export default WeeklyScheduleGenerator;
